/**
 * Settings — how the dashboard is configured, and whether its inputs are
 * healthy.
 *
 * This is where the "assumptions" note from the Availability screen moved to.
 * It was doing two jobs badly there: explaining how capacity is calculated
 * (reference material, read once) and hinting that things could be configured
 * (they couldn't). Both belong here, next to the controls that change them.
 */

import { useEffect, useState } from 'react';
import type {
  BusinessHours,
  IntegrationHealthReport,
  IntegrationStatus,
} from '@cer-autofill/shared';
import { DEFAULT_BUSINESS_HOURS } from '@cer-autofill/shared';
import {
  apiErrorReason,
  fetchIntegrationHealth,
  fetchOrgAvailabilitySettings,
  saveOrgAvailabilitySettings,
} from '../api.js';
import { getSession } from '../session.js';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function clock(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

function minutesFromClock(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

const STATUS_LABELS: Record<IntegrationStatus, string> = {
  connected: 'Connected',
  degraded: 'Not responding',
  'not-configured': 'Not connected',
  disabled: 'Ignored by choice',
};

export function SettingsScreen() {
  const [hours, setHours] = useState<BusinessHours>(DEFAULT_BUSINESS_HOURS);
  const [updatedAt, setUpdatedAt] = useState<string | undefined>();
  const [updatedBy, setUpdatedBy] = useState<string | undefined>();
  const [health, setHealth] = useState<IntegrationHealthReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = (getSession()?.roles ?? []).includes('admin');

  useEffect(() => {
    setLoading(true);
    void Promise.all([fetchOrgAvailabilitySettings(), fetchIntegrationHealth()])
      .then(([settings, report]) => {
        setHours(settings.businessHours);
        setUpdatedAt(settings.updatedAt);
        setUpdatedBy(settings.updatedBy);
        setHealth(report);
      })
      .catch((err: unknown) => setError(apiErrorReason(err)))
      .finally(() => setLoading(false));
  }, []);

  const invalid = hours.endMinutes <= hours.startMinutes || hours.workingDays.length === 0;

  function toggleDay(day: number) {
    setSaved(false);
    setHours((value) => ({
      ...value,
      workingDays: value.workingDays.includes(day)
        ? value.workingDays.filter((item) => item !== day)
        : [...value.workingDays, day].sort((a, b) => a - b),
    }));
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const result = await saveOrgAvailabilitySettings({ businessHours: hours });
      setUpdatedAt(result.updatedAt);
      setUpdatedBy(result.updatedBy);
      setSaved(true);
    } catch (err) {
      setError(apiErrorReason(err));
    } finally {
      setSaving(false);
    }
  }

  const dayLength = Math.max(0, hours.endMinutes - hours.startMinutes) / 60;

  return (
    <main className="screen settings-screen">
      <div className="screen__header">
        <div>
          <h2>Settings</h2>
          <p className="subtitle">Business hours and the integrations behind the dashboard</p>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {loading ? <p className="loading">Loading settings…</p> : null}

      <section className="overview-band" aria-label="Business hours">
        <div className="band-heading">
          <div>
            <h3>Normal business hours</h3>
            <p className="muted">
              The working window every capacity figure is measured against. Leave and bookings are
              clipped to it, so a 6am start on a job outside these hours does not consume the day.
            </p>
          </div>
        </div>

        <div className="filter-grid">
          <label>
            <span>Day starts</span>
            <input
              type="time"
              value={clock(hours.startMinutes)}
              disabled={!isAdmin}
              onChange={(e) => {
                setSaved(false);
                setHours((value) => ({ ...value, startMinutes: minutesFromClock(e.target.value) }));
              }}
            />
          </label>
          <label>
            <span>Day ends</span>
            <input
              type="time"
              value={clock(hours.endMinutes)}
              disabled={!isAdmin}
              onChange={(e) => {
                setSaved(false);
                setHours((value) => ({ ...value, endMinutes: minutesFromClock(e.target.value) }));
              }}
            />
          </label>
        </div>

        <fieldset className="settings-days" disabled={!isAdmin}>
          <legend>Working days</legend>
          {DAY_LABELS.map((label, day) => (
            <label key={label} className="settings-days__day">
              <input
                type="checkbox"
                checked={hours.workingDays.includes(day)}
                onChange={() => toggleDay(day)}
              />
              <span>{label}</span>
            </label>
          ))}
        </fieldset>

        <p className="muted">
          {invalid
            ? 'Pick at least one working day, and an end time after the start.'
            : `A full day is ${dayLength % 1 ? dayLength.toFixed(1) : dayLength}h. Days not selected carry no capacity at all.`}
        </p>

        {isAdmin ? (
          <div className="filter-actions">
            <button type="button" disabled={saving || invalid} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save business hours'}
            </button>
            {saved ? <span className="muted">Saved.</span> : null}
          </div>
        ) : (
          <p className="muted">Only an administrator can change business hours.</p>
        )}

        {updatedAt ? (
          <p className="muted">
            Last changed {new Date(updatedAt).toLocaleString()}
            {updatedBy ? ` by ${updatedBy}` : ''}.
          </p>
        ) : null}
      </section>

      <section className="overview-band" aria-label="Integrations">
        <div className="band-heading">
          <div>
            <h3>Integrations</h3>
            <p className="muted">Where availability comes from, and whether it is reaching us.</p>
          </div>
        </div>

        <div className="settings-integrations">
          {(health?.integrations ?? []).map((integration) => (
            <article className="settings-integration" key={integration.id}>
              <header>
                <h4>{integration.label}</h4>
                <span className={`status-pill status-pill--${integration.status}`}>
                  {STATUS_LABELS[integration.status]}
                </span>
              </header>
              <p className="muted">{integration.description}</p>
              {integration.detail ? <p className="muted">{integration.detail}</p> : null}
              {integration.checks.length ? (
                <ul className="settings-integration__checks">
                  {integration.checks.map((check) => (
                    <li key={check.label}>
                      <span
                        className={`status-dot status-dot--${check.status}`}
                        aria-hidden="true"
                      />
                      <span>{check.label}</span>
                      <span className="muted">{STATUS_LABELS[check.status]}</span>
                      {check.detail ? <span className="muted">{check.detail}</span> : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </article>
          ))}
        </div>
      </section>

      <section className="overview-band" aria-label="How availability is calculated">
        <div className="band-heading">
          <div>
            <h3>How availability is calculated</h3>
          </div>
        </div>
        <ul className="settings-notes">
          <li>
            Capacity starts at the business hours above, then bookings and unavailability are
            deducted. Overlapping ones are only counted once.
          </li>
          <li>
            ServiceM8 is read-only. Availability declared here is stored in the dashboard; nothing
            is written back to ServiceM8 yet.
          </li>
          <li>
            Reasons are never shown. A block records when someone is unavailable, not why — if the
            reason matters, it stays in ServiceM8 behind that system&apos;s own permissions.
          </li>
          <li>
            ACTNOW bookings and leave are counted for inspectors linked to both companies, but the
            detail is redacted.
          </li>
          <li>Employment Hero leave is not included yet.</li>
        </ul>
      </section>
    </main>
  );
}
